import type { CrossCuttingTopic, LocalizedText, OrgLanguage, OrgUnit } from '../types';

/** Sprachen für mehrsprachige Bezeichnungen, in fester Reihenfolge (docs/07, 2.2). */
export const TEXT_LANGUAGES: OrgLanguage[] = ['de', 'fr', 'it', 'en'];

export const LANGUAGE_LABELS: Record<OrgLanguage, string> = {
    de: 'Deutsch',
    fr: 'Français',
    it: 'Italiano',
    en: 'English'
};

/** Kurzcode der UI-Sprache ("de-CH" -> "de"). */
export function shortLanguage(lang: string | undefined): OrgLanguage {
    const short = (lang || 'de').slice(0, 2) as OrgLanguage;
    return TEXT_LANGUAGES.includes(short) ? short : 'de';
}

/** Aktive Mandantensprachen in fester Reihenfolge; ohne Einstellung alle vier. */
export function activeLanguages(available?: string[]): OrgLanguage[] {
    const active = TEXT_LANGUAGES.filter(lang => available?.includes(lang));
    return active.length > 0 ? active : TEXT_LANGUAGES;
}

/** Entfernt leere Sprachen. */
export function cleanLocalized(texts: LocalizedText | undefined): LocalizedText {
    const clean: LocalizedText = {};
    for (const lang of TEXT_LANGUAGES) {
        const value = texts?.[lang]?.trim();
        if (value) clean[lang] = value;
    }
    return clean;
}

/**
 * Texte zum Bearbeiten: vorhandene Sprachen, sonst der bisherige Text ohne Sprache
 * in der ersten Mandantensprache (Daten von vor der Mehrsprachigkeit).
 */
export function editableTexts(texts: LocalizedText | undefined, legacy: string | undefined, languages: OrgLanguage[]): LocalizedText {
    const clean = cleanLocalized(texts);
    if (Object.keys(clean).length > 0 || !legacy) return clean;
    return { [languages[0] || 'de']: legacy };
}

/** Einheit mit Name und Kürzel in der gewünschten Sprache; fehlt sie, gilt der Anzeigename des Servers. */
export function localizeOrgUnit(unit: OrgUnit, lang: OrgLanguage): OrgUnit {
    return { ...unit, name: unit.names?.[lang] || unit.name, code: unit.codes?.[lang] || unit.code };
}

/** Querschnittsthema mit Name und Beschreibung in der gewünschten Sprache. */
export function localizeTopic(topic: CrossCuttingTopic, lang: OrgLanguage): CrossCuttingTopic {
    return {
        ...topic,
        name: topic.names?.[lang] || topic.name,
        description: topic.descriptions?.[lang] || topic.description
    };
}

/**
 * Sprachen, in denen der Name fehlt, obwohl der Mandant sie aktiviert hat und sie für andere Einheiten
 * bereits nutzt. Ein Mandant, der nur deutsch benennt, erhält so keine Hinweise.
 */
export function missingNameLanguages(unit: OrgUnit, languagesInUse: Set<OrgLanguage>, active: OrgLanguage[]): OrgLanguage[] {
    if (!unit.names || Object.keys(unit.names).length === 0) return [];
    return active.filter(lang => languagesInUse.has(lang) && !unit.names?.[lang]);
}

/** Name einer Einheit samt Kürzel, z.B. "Amt für Informatik (AFI)". */
export function formatOrgUnit(unit: OrgUnit | undefined | null): string {
    if (!unit) return '';
    return unit.code ? `${unit.name} (${unit.code})` : unit.name;
}
